import React, { useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Database, AlertTriangle, CheckCircle2, Play, RefreshCw, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const DataMigrationsPage = () => {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);

  if (user?.role !== 'super_admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-rose-400 mx-auto mb-3" />
          <p className="text-lg font-semibold text-slate-800">Acesso restrito</p>
          <p className="text-sm text-slate-500 mt-1">Esta página é exclusiva para Super Admin.</p>
        </div>
      </Layout>
    );
  }

  const runSimulate = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API_URL}/admin/backfill-partner-id?dry_run=true`);
      setReport(r.data);
      toast.success(`Simulação concluída: ${r.data.total} contratos analisados`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a simular');
    } finally {
      setRunning(false);
    }
  };

  const runApply = async () => {
    if (!report || (report.resolved_via_angariador + report.resolved_via_lead === 0)) {
      toast.error('Nada a aplicar. Corre a simulação primeiro.');
      return;
    }
    if (!window.confirm(`Confirma a aplicação?\n\nVai preencher partner_id em ${report.resolved_via_angariador + report.resolved_via_lead} contratos.\nContratos com partner_id já definido NÃO são alterados.`)) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API_URL}/admin/backfill-partner-id?dry_run=false`);
      setReport(r.data);
      toast.success(`✅ Aplicado em ${r.data.resolved_via_angariador + r.data.resolved_via_lead} contratos`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a aplicar');
    } finally {
      setApplying(false);
    }
  };

  const resolvedCount = report ? (report.resolved_via_angariador + report.resolved_via_lead) : 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="data-migrations-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
            <Database className="w-6 h-6 text-[#2D3E6F]" /> Migrações de Dados
          </h1>
          <p className="text-slate-500 mt-1">Ferramentas administrativas para correcção em massa de dados. Acesso restrito.</p>
        </div>

        <Card className="rounded-2xl border-slate-100">
          <CardHeader>
            <CardTitle className="font-outfit text-base flex items-center justify-between">
              <span>Atribuição de Parceiro a Contratos</span>
              {report?.applied && (
                <span className="text-xs font-semibold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Aplicado
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 space-y-2">
              <p><strong>O que faz:</strong> preenche o campo <code className="bg-white px-1 py-0.5 rounded text-xs">partner_id</code> em contratos onde está vazio.</p>
              <p><strong>Como determina:</strong></p>
              <ol className="list-decimal pl-5 space-y-0.5">
                <li>Se o contrato tem <code className="bg-white px-1 py-0.5 rounded text-xs">angariador_id</code> → pega o <code className="bg-white px-1 py-0.5 rounded text-xs">partner_id</code> desse utilizador</li>
                <li>Senão, se tem <code className="bg-white px-1 py-0.5 rounded text-xs">lead_id</code> → pega o angariador da lead → pega o <code className="bg-white px-1 py-0.5 rounded text-xs">partner_id</code></li>
                <li>Senão fica em branco (não inventa)</li>
              </ol>
              <p><strong>Segurança:</strong> contratos com <code className="bg-white px-1 py-0.5 rounded text-xs">partner_id</code> já definido NÃO são alterados. Cada alteração regista uma activity no contrato.</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={runSimulate} disabled={running} variant="outline" data-testid="btn-simulate">
                {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Simular (Dry-Run)
              </Button>
              <Button
                onClick={runApply}
                disabled={applying || !report || resolvedCount === 0 || report.applied}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="btn-apply"
              >
                {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                Aplicar ({resolvedCount} contratos)
              </Button>
            </div>

            {report && (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="migration-report">
                <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-200">
                  <div className="p-3 text-center">
                    <p className="text-2xl font-bold text-slate-800">{report.total}</p>
                    <p className="text-xs text-slate-500">Total a analisar</p>
                  </div>
                  <div className="p-3 text-center bg-emerald-50">
                    <p className="text-2xl font-bold text-emerald-700">{report.resolved_via_angariador}</p>
                    <p className="text-xs text-emerald-600">Via Angariador</p>
                  </div>
                  <div className="p-3 text-center bg-sky-50">
                    <p className="text-2xl font-bold text-sky-700">{report.resolved_via_lead}</p>
                    <p className="text-xs text-sky-600">Via Lead</p>
                  </div>
                  <div className="p-3 text-center bg-slate-50">
                    <p className="text-2xl font-bold text-slate-500">{report.unresolved}</p>
                    <p className="text-xs text-slate-400">Sem dados</p>
                  </div>
                </div>

                {report.examples.length > 0 && (
                  <div className="border-t border-slate-200 max-h-[420px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-600 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Código</th>
                          <th className="px-3 py-2 text-left">Cliente</th>
                          <th className="px-3 py-2 text-left">Parceiro Detectado</th>
                          <th className="px-3 py-2 text-left">Via</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {report.examples.map(ex => (
                          <tr key={ex.contract_id} data-testid={`migration-row-${ex.codigo}`}>
                            <td className="px-3 py-2 font-mono text-xs text-slate-500">#{ex.codigo}</td>
                            <td className="px-3 py-2">{ex.nome_cliente}</td>
                            <td className="px-3 py-2 font-medium text-slate-800">{ex.partner_name}</td>
                            <td className="px-3 py-2">
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ex.via === 'angariador' ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'}`}>
                                {ex.via}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {resolvedCount > report.examples.length && (
                      <p className="text-xs text-slate-400 text-center py-2 bg-slate-50">
                        ... mais {resolvedCount - report.examples.length} contratos (exibidos apenas os primeiros {report.examples.length})
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DataMigrationsPage;
